import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_DUPLICATE_CUSTOM_SPACE,
  isDuplicateSpace,
  isPreNamedSpace,
  shouldDisplayAsRatio,
} from 'spw-components/worksheet/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  projectSpaceTypes as projectSpaceTypesSelector,
  selectCurrentUserIsViewOnly,
} from 'spw-redux/modules/projects/selectors';
import {
  selectAllChildWorksheetSpaces,
  selectCurrentScenario,
  selectIsRowDisabled,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { IChildWorksheetSpaceType } from 'spw-redux/modules/calculators/types';
import {
  limitInputValue,
  useRowSizeInputs,
} from 'spw-programming-projects/building-details/worksheet/utils';
import {
  onBlurQuantity,
  onBlurRatio,
  onBlurSeatCap,
  onBlurUsf,
} from 'spw-shared/worksheet-calculation-utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { groupBy } from 'lodash';
import { useWorksheetRowsInUse } from 'spw-components/worksheet/utils/hooks';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { MeasurementSystem, Unit } from 'spw-components/unit-conversion/types';
import { toFixedWithRounding } from 'spw-shared/utils';
import { deleteWorksheetRow } from 'spw-redux/modules/worksheet/actions';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import WorksheetRow from '../../../../models/WorksheetRow';
import { PlainWorksheet } from '../../../../models/Worksheet';

type WorksheetRowKey = keyof WorksheetRow;

type Props = {
  row: WorksheetRow;
  inputRef: React.RefObject<HTMLInputElement>;
  onRowNameFocus?: () => void;
  onRowNameBlur?: () => void;
  changeRow?: (
    row: WorksheetRow,
    options?: {
      name?: string;
      isNameUpdate?: boolean;
      isCustomSpaceType?: boolean;
    }
  ) => void;
  isCustomSpaceType: boolean;
  clientFacingNamesAreVisible?: boolean;
  isPerDepartmentRow?: boolean;
  active?: boolean;
};

export const useSpaceTypeRowUI = ({
  row,
  inputRef,
  onRowNameFocus,
  onRowNameBlur,
  changeRow,
  isCustomSpaceType,
  clientFacingNamesAreVisible,
  isPerDepartmentRow,
  active,
}: Props) => {
  const { name: spaceTypeName } = row.spaceType;
  const scenario = useSelector(selectCurrentScenario);
  const projectSpaceTypes = useSelector(projectSpaceTypesSelector);
  const childWorksheetSpaces: IChildWorksheetSpaceType[] = useSelector(
    selectAllChildWorksheetSpaces
  );

  const [editValue, setEditValue] = useState('');
  const [editFactor, setEditFactor] = useState('');
  const initialEditValue = useRef('');
  const [isDirty, setIsDirty] = useState(false);
  const [isHoveringName, setIsHoveringName] = useState(false);
  const [name, setName] = useState(spaceTypeName);
  const [hover, setHover] = useState(false);
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const { convertToOriginalUnit } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);

  const isRatio = shouldDisplayAsRatio(
    row.projectSpaceTypeCategory,
    row.projectSpaceTypeSubcategory
  );

  const { workSeats: totalWorkSeats } = scenario;

  const isDisabled = useSelector(selectIsRowDisabled);
  const isEditingName = editFactor === 'name';

  const spaceNameIsDuplicated =
    editValue?.toLowerCase() === DEFAULT_DUPLICATE_CUSTOM_SPACE.toLowerCase();

  const handleInputChange = useCallback((value: string, maxValue: number) => {
    setIsDirty(true);
    setEditValue(limitInputValue(value, maxValue));
  }, []);

  const blurCurrentInput = useCallback(() => {
    setEditValue('');
    setEditFactor('');
    setIsDirty(false);
    initialEditValue.current = '';
  }, []);

  const onFocusRowName = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();

      setIsHoveringName(false);

      if (
        isDisabled ||
        (!clientFacingNamesAreVisible && !isCustomSpaceType && !row.child) ||
        currentUserIsViewOnly
      ) {
        return {};
      }

      onRowNameFocus();
      if (
        editFactor !== 'name' ||
        editValue === DEFAULT_DUPLICATE_CUSTOM_SPACE
      ) {
        const timeout = setTimeout(() => {
          const currentInput = inputRef?.current;
          if (currentInput) {
            currentInput.focus();
          }
        }, 100);

        if (name) {
          setEditValue(name);
        } else {
          setEditValue('');
        }

        setEditFactor(`name`);

        return () => {
          clearTimeout(timeout);
        };
      }

      return {};
    },
    [
      editFactor,
      editValue,
      inputRef,
      name,
      clientFacingNamesAreVisible,
      isCustomSpaceType,
      row?.child,
      onRowNameFocus,
      currentUserIsViewOnly,
      isDisabled,
    ]
  );

  const onBlurNameInput = useCallback(() => {
    onRowNameBlur();

    if (
      editValue.trim() ||
      (clientFacingNamesAreVisible && !isCustomSpaceType)
    ) {
      const nameIsDuplicated = isDuplicateSpace(
        projectSpaceTypes,
        childWorksheetSpaces,
        editValue,
        row.spaceType.id
      );
      if (
        editValue.toLowerCase() ===
          DEFAULT_DUPLICATE_CUSTOM_SPACE.toLowerCase() ||
        nameIsDuplicated ||
        isPreNamedSpace(editValue)
      ) {
        setEditValue(DEFAULT_DUPLICATE_CUSTOM_SPACE);
      } else {
        changeRow(
          {
            ...row,
          },
          { name: editValue.trim(), isNameUpdate: true, isCustomSpaceType }
        );
        if (editValue.trim()) setName(editValue.trim());
        blurCurrentInput();
      }
    } else {
      blurCurrentInput();
    }
  }, [
    changeRow,
    editValue,
    projectSpaceTypes,
    childWorksheetSpaces,
    row,
    blurCurrentInput,
    isCustomSpaceType,
    clientFacingNamesAreVisible,
    onRowNameBlur,
  ]);

  const onBlurRatioInput = useCallback(() => {
    const { newQuantity, rowSeatCap } = onBlurRatio({
      row,
      currentRatio: row.ratio,
      editValue,
      totalWorkSeats,
      isDirty,
      isRatio,
    });

    if (newQuantity !== row.quantity) {
      changeRow({
        ...row,
        quantity: newQuantity,
        seatCap: rowSeatCap,
      });
    }
    blurCurrentInput();
  }, [
    changeRow,
    blurCurrentInput,
    row,
    editValue,
    totalWorkSeats,
    isDirty,
    isRatio,
  ]);

  const onBlurQuantityInput = useCallback(() => {
    const { newQuantity, rowSeatCap } = onBlurQuantity({
      row,
      editValue,
      isDirty,
    });
    if (newQuantity !== row.quantity)
      changeRow({
        ...row,
        quantity: newQuantity,
        seatCap: rowSeatCap,
      });

    blurCurrentInput();
  }, [row, editValue, isDirty, blurCurrentInput, changeRow]);

  const onFocusRowInput = useCallback(
    (
      rowField: keyof WorksheetRow,
      event?: React.FocusEvent<HTMLInputElement>
    ) => {
      let newEditValue = '';
      if (event) {
        newEditValue = event.target.value
          ? event.target.value.replace(/,/g, '')
          : '';
      } else {
        newEditValue = row[rowField]
          ? (row[rowField] as number).toFixed(2)
          : '';
      }
      initialEditValue.current = newEditValue;
      setEditValue(newEditValue);
      setEditFactor(`${name}.${rowField}`);
    },
    [row, name]
  );

  const onBlurSeatCapInput = useCallback(() => {
    const { newSeatCap } = onBlurSeatCap({
      editValue,
      row,
      isDirty,
    });
    if (newSeatCap !== row.seatCap)
      changeRow({
        ...row,
        seatCap: newSeatCap,
      });
    blurCurrentInput();
  }, [changeRow, blurCurrentInput, row, editValue, isDirty]);

  const unitConvertedEditValue = useMemo(() => {
    const USFInputIsBeingEdited = editFactor.includes('usf');
    let inputUnit = Unit.feet;

    if (
      measurementSystem === MeasurementSystem.metric &&
      !USFInputIsBeingEdited
    ) {
      inputUnit = Unit.millimeters; // dimension inputs will use mm for metric system
    } else if (
      measurementSystem === MeasurementSystem.metric &&
      USFInputIsBeingEdited
    ) {
      inputUnit = Unit.meters; // USF inputs will use meters for metric system
    }

    return convertToOriginalUnit(parseFloat(editValue), {
      unitType: USFInputIsBeingEdited ? 'area' : 'linear',
      inputUnit,
      outputUnit: Unit.feet,
    }).toString();
  }, [editValue, convertToOriginalUnit, editFactor, measurementSystem]);

  const onBlurUsfInput = useCallback(() => {
    if (editValue !== initialEditValue.current) {
      const newRow = onBlurUsf(row, unitConvertedEditValue, isDirty);

      if (newRow.usf !== row.usf) {
        changeRow(newRow);
      }
    }
    blurCurrentInput();
  }, [
    row,
    isDirty,
    changeRow,
    blurCurrentInput,
    unitConvertedEditValue,
    editValue,
  ]);

  const { onBlurSizeInput } = useRowSizeInputs({
    editFactor,
    editValue: unitConvertedEditValue,
    row,
    usfFieldKey: 'usf',
  });

  const handleSizeInputBlurred = useCallback(
    (fieldId: WorksheetRowKey) => {
      const updatedRow: WorksheetRow = onBlurSizeInput();

      if (updatedRow[fieldId] !== row[fieldId]) changeRow(updatedRow);
      blurCurrentInput();
    },
    [changeRow, blurCurrentInput, onBlurSizeInput, row]
  );

  const className = useMemo(() => {
    if (editFactor !== '') {
      return 'active';
    }

    if (hover || active) {
      return 'hover';
    }

    if (isPerDepartmentRow) {
      return 'white-background';
    }
    return '';
  }, [isPerDepartmentRow, editFactor, hover, active]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        (e.target as HTMLInputElement).blur();
      } else if (editFactor !== '') {
        setIsDirty(true);
      }
    },
    [editFactor]
  );

  return {
    editFactor,
    editValue,
    name,
    setName,
    setEditValue,
    setHover,
    isHoveringName,
    setIsHoveringName,
    onFocusRowName,
    onFocusRowInput,
    onBlurNameInput,
    onBlurRatioInput,
    onBlurQuantityInput,
    onBlurUsfInput,
    handleSizeInputBlurred,
    handleInputChange,
    onBlurSeatCapInput,
    handleKeyPress,
    className,
    isEditingName,
    isRatio,
    spaceNameIsDuplicated,
    isDisabled,
  };
};

export const useChildRowsBySpaceType = (includeRemovedRows = false) => {
  const worksheet = useSelector(selectWorksheet);
  const worksheetRowsInUse = useWorksheetRowsInUse();
  const worksheetRows = includeRemovedRows
    ? worksheet.rows
    : worksheetRowsInUse;

  const childWorksheetSpaceTypes: IChildWorksheetSpaceType[] = useSelector(
    selectAllChildWorksheetSpaces
  );

  const childWorksheetSpaceTypesById: {
    [key: number]: IChildWorksheetSpaceType;
  } = useMemo(() => {
    return childWorksheetSpaceTypes.reduce(
      (map, childWorksheetSpaceType: IChildWorksheetSpaceType) => {
        return {
          ...map,
          [childWorksheetSpaceType.id]: childWorksheetSpaceType,
        };
      },
      {}
    );
  }, [childWorksheetSpaceTypes]);

  const childRowsBySpaceType = useMemo(() => {
    return groupBy(
      worksheetRows.filter((worksheetRow: WorksheetRow) => worksheetRow.child),
      (worksheetRow: WorksheetRow) => {
        const id = worksheetRow.spaceId || worksheetRow.spaceType.id;
        const childWorksheetSpace = childWorksheetSpaceTypesById[id];
        return childWorksheetSpace?.parent?.id;
      }
    );
  }, [worksheetRows, childWorksheetSpaceTypesById]);

  return childRowsBySpaceType;
};

export const useQuantityRounding = ({
  quantityInputRef,
  setEditValue,
  editValue,
}: {
  quantityInputRef?: RefObject<HTMLInputElement>;
  setEditValue: (s: string) => void;
  editValue: string;
}) => {
  const quantityHasBeenRounded = useRef(false);

  useEffect(() => {
    if (quantityHasBeenRounded.current) {
      quantityInputRef.current.blur();
      quantityHasBeenRounded.current = false;
    }
  }, [editValue, quantityInputRef]);

  const handleRoundQuantity = useCallback(
    (e: React.MouseEvent, direction: 'down' | 'up') => {
      e.stopPropagation();
      let value = parseFloat(editValue);
      value = direction === 'up' ? Math.ceil(value) : Math.floor(value);
      setEditValue(toFixedWithRounding(value, 2));
      quantityHasBeenRounded.current = true;
    },
    [editValue, setEditValue]
  );

  return {
    handleRoundQuantity,
  };
};

export const filterWorksheetRowsByCategory = (
  worksheet: PlainWorksheet,
  category: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory
) => {
  return (
    (category.parentId
      ? worksheet.rowsBySubcategory[category.defaultSpaceTypeCategory.category]
      : worksheet.rowsByCategory[category.defaultSpaceTypeCategory.category]) ||
    []
  );
};

export const groupRowsBySpaceType = (rows: WorksheetRow[]) => {
  return (rows || [])
    .filter((r: WorksheetRow) => !r.child)
    .reduce((map, row: WorksheetRow) => {
      return {
        ...map,
        [row.projectSpaceType.id]: row,
      };
    }, {});
};

export const groupRowsByChildSpaceType = (rows: WorksheetRow[]) => {
  return (rows || [])
    .filter((r: WorksheetRow) => r.child)
    .reduce((map, row: WorksheetRow) => {
      return {
        ...map,
        [row.spaceId]: row,
      };
    }, {});
};

export const useCategoryRowsBySpaceType = (
  category: IProjectSpaceTypeCategory | IProjectSpaceTypeSubcategory
) => {
  const worksheet = useSelector(selectWorksheet);
  return useMemo(() => {
    const rows = filterWorksheetRowsByCategory(worksheet, category);
    return groupRowsBySpaceType(rows);
  }, [worksheet, category]);
};

export const useDeleteWorksheetRow = () => {
  const worksheet = useSelector(selectWorksheet);
  const dispatch = useDispatch();

  return useCallback(
    (projectSpaceType: IProjectSpaceType) => {
      const { rows } = worksheet;
      const rowToDelete = rows.find(
        (row) => row.projectSpaceType?.id === projectSpaceType?.id
      );

      dispatch(deleteWorksheetRow(rowToDelete));
    },
    [dispatch, worksheet]
  );
};

export const useDeleteChildRow = () => {
  const worksheet = useSelector(selectWorksheet);
  const dispatch = useDispatch();

  return useCallback(
    (childWorksheetSpaceType: IChildWorksheetSpaceType) => {
      const { rows } = worksheet;
      const rowToDelete = rows.find(
        (row) => row.child && row.spaceId === childWorksheetSpaceType.id
      );

      dispatch(deleteWorksheetRow(rowToDelete));
    },
    [dispatch, worksheet]
  );
};
